import styled, { DefaultTheme } from "styled-components";
import { Card, Typography, Button, Space } from "antd";
import { Swiper, SwiperSlide } from "swiper/react";

const { Title, Text } = Typography;

// Declare module augmentation for styled-components theme
declare module "styled-components" {
  export interface DefaultTheme {
    mode?: "light" | "dark";
  }
}

// Theme helper function
const getThemeValue = <T,>(props: { theme?: DefaultTheme }, darkValue: T, lightValue: T): T => {
  return props.theme?.mode === "dark" ? darkValue : lightValue;
};

// Using existing app colors - no gradients needed

export const SummaryCard = styled(Card)`
  margin-bottom: 32px;
  background: ${(props) => getThemeValue(props, "#2D3748", "#ffffff")} !important;
  border-radius: 16px;
  border: ${(props) => getThemeValue(props, "1px solid #4A5568", "1px solid #E2E8F0")};
  box-shadow: ${(props) => getThemeValue(
    props,
    "0 4px 20px rgba(0, 0, 0, 0.3), 0 1px 3px rgba(0, 0, 0, 0.2)",
    "0 4px 20px rgba(0, 0, 0, 0.08), 0 1px 3px rgba(0, 0, 0, 0.05)"
  )};
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  overflow: hidden;
  position: relative;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, #1890ff 0%, #722ed1 50%, #fa8c16 100%);
    opacity: 0.8;
    z-index: 1;
  }

  &:hover {
    box-shadow: ${(props) => getThemeValue(
      props,
      "0 8px 30px rgba(0, 0, 0, 0.4), 0 2px 6px rgba(0, 0, 0, 0.3)",
      "0 8px 30px rgba(0, 0, 0, 0.12), 0 2px 6px rgba(0, 0, 0, 0.08)"
    )};
  }

  .ant-card-body {
    padding: 32px;
    background: transparent !important;
  }

  .ant-typography {
    color: ${(props) => getThemeValue(props, "#ffffff", "#1A202C")} !important;
  }
`;

export const MetricsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 20px;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  padding: 24px 0;
  align-items: stretch;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 16px;
    padding: 16px 0;
  }

  @media (min-width: 769px) and (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 18px;
  }

  @media (min-width: 1025px) {
    grid-template-columns: repeat(4, 1fr);
    gap: 20px;
  }
`;

export const MetricCard = styled(Card)`
  background: ${(props) => getThemeValue(props, "#2D3748", "#ffffff")} !important;
  border: ${(props) => getThemeValue(props, "1px solid #4A5568", "1px solid #E2E8F0")};
  border-radius: 12px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  cursor: pointer;
  position: relative;
  overflow: hidden;
  height: 100%;
  display: flex;
  flex-direction: column;
  box-shadow: ${(props) => getThemeValue(
    props,
    "0 2px 8px rgba(0, 0, 0, 0.2)",
    "0 2px 8px rgba(0, 0, 0, 0.06)"
  )};

  &::after {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: linear-gradient(90deg, transparent 0%, currentColor 50%, transparent 100%);
    opacity: 0;
    transition: opacity 0.3s ease;
  }

  &:hover {
    transform: translateY(-2px);
    background: ${(props) => getThemeValue(props, "#4A5568", "#ffffff")} !important;
    box-shadow: ${(props) => getThemeValue(
      props,
      "0 12px 24px rgba(0, 0, 0, 0.4), 0 4px 8px rgba(0, 0, 0, 0.3)",
      "0 12px 24px rgba(0, 0, 0, 0.12), 0 4px 8px rgba(0, 0, 0, 0.08)"
    )};
    border-color: ${(props) => getThemeValue(props, "#4A5568", "rgba(24, 144, 255, 0.3)")};

    &::after {
      opacity: 1;
    }
  }

  .ant-card-body {
    padding: 20px;
    background: transparent !important;
    display: flex;
    flex-direction: column;
    flex: 1;
    overflow: hidden;
  }

  .ant-typography {
    color: ${(props) => getThemeValue(props, "#ffffff", "#1A202C")} !important;
    word-wrap: break-word;
    overflow-wrap: break-word;
    white-space: normal;
  }
`;

export const RankingCard = styled(Card)`
  background: ${(props) => getThemeValue(props, "#2D3748", "#ffffff")} !important;
  border-radius: 16px;
  text-align: center;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  border: ${(props) => getThemeValue(props, "1px solid #4A5568", "1px solid #E2E8F0")};
  box-shadow: ${(props) => getThemeValue(
    props,
    "0 4px 12px rgba(0, 0, 0, 0.3)",
    "0 4px 12px rgba(0, 0, 0, 0.08)"
  )};
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    top: -50%;
    left: -50%;
    width: 200%;
    height: 200%;
    background: radial-gradient(circle, rgba(24, 144, 255, 0.1) 0%, transparent 70%);
    opacity: 0;
    transition: opacity 0.3s ease;
  }

  &:hover {
    transform: translateY(-2px);
    background: ${(props) => getThemeValue(props, "#4A5568", "#ffffff")} !important;
    box-shadow: ${(props) => getThemeValue(
      props,
      "0 12px 28px rgba(0, 0, 0, 0.5), 0 4px 12px rgba(0, 0, 0, 0.3)",
      "0 12px 28px rgba(0, 0, 0, 0.15), 0 4px 12px rgba(0, 0, 0, 0.1)"
    )};

    &::before {
      opacity: 1;
    }
  }

  .ant-card-body {
    padding: 24px;
    background: transparent !important;
  }

  .ant-typography {
    color: ${(props) => getThemeValue(props, "#ffffff", "#1A202C")} !important;
  }
`;

export const ChartContainer = styled.div`
  border-radius: 16px;
  min-height: 450px;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  background: ${(props) => getThemeValue(props, "#2D3748", "#ffffff")} !important;
  border: ${(props) => getThemeValue(props, "1px solid #4A5568", "1px solid #E2E8F0")};
  box-shadow: ${(props) => getThemeValue(
    props,
    "0 4px 20px rgba(0, 0, 0, 0.3), 0 1px 3px rgba(0, 0, 0, 0.2)",
    "0 4px 20px rgba(0, 0, 0, 0.08), 0 1px 3px rgba(0, 0, 0, 0.05)"
  )};
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  overflow: hidden;
  position: relative;
  padding: 0;
  margin: 0;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: linear-gradient(90deg, #1890ff 0%, #722ed1 50%, #fa8c16 100%);
    opacity: 0.6;
    z-index: 1;
  }

  /* Ensure chart area has proper background */
  > div:not([class*="ant-dropdown"]) {
    background: ${(props) => getThemeValue(props, "#2D3748", "#ffffff")} !important;
    max-width: 100%;
    overflow-x: auto;
  }
  
  /* Ensure dropdowns can render outside */
  .ant-dropdown,
  .ant-picker-dropdown,
  [class*="ant-select-dropdown"] {
    z-index: 1050 !important;
  }

  /* Force text colors in chart container */
  .ant-typography {
    color: ${(props) => getThemeValue(props, "#ffffff", "#1A202C")} !important;
  }
`;

export const RankNumber = styled(Title)<{ rank: number }>`
  margin-bottom: 16px !important;
  font-size: 3rem !important;
  font-weight: 800 !important;
  background: ${(props) => {
    switch (props.rank) {
      case 1:
        return "linear-gradient(135deg, #ffd700 0%, #ffed4e 50%, #ffa500 100%)";
      case 2:
        return "linear-gradient(135deg, #c0c0c0 0%, #e8e8e8 50%, #a8a8a8 100%)";
      case 3:
        return "linear-gradient(135deg, #cd7f32 0%, #ff8c69 50%, #ff6347 100%)";
      default:
        return "linear-gradient(135deg, #9e9e9e 0%, #bdbdbd 50%, #757575 100%)";
    }
  }};
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.2));
  transition: all 0.3s ease;

  &:hover {
    transform: scale(1.1);
  }
`;

export const CategoryImage = styled.img`
  border-radius: 50%;
  width: 120px;
  height: 120px;
  object-fit: cover;
  margin-bottom: 16px;
  border: ${(props) => getThemeValue(props, "3px solid #4A5568", "3px solid #ffffff")};
  box-shadow: ${(props) => getThemeValue(
    props,
    "0 4px 12px rgba(0, 0, 0, 0.5), 0 0 0 4px rgba(24, 144, 255, 0.2)",
    "0 4px 12px rgba(0, 0, 0, 0.15), 0 0 0 4px rgba(24, 144, 255, 0.1)"
  )};
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

  &:hover {
    transform: scale(1.1) rotate(5deg);
    box-shadow: ${(props) => getThemeValue(
      props,
      "0 8px 20px rgba(0, 0, 0, 0.6), 0 0 0 6px rgba(24, 144, 255, 0.3)",
      "0 8px 20px rgba(0, 0, 0, 0.2), 0 0 0 6px rgba(24, 144, 255, 0.15)"
    )};
  }
`;

export const StyledTitle = styled(Title)`
  color: ${(props) => getThemeValue(props, "#ffffff", "#1A202C")} !important;
  font-weight: 600 !important;
  letter-spacing: -0.02em;
  
  &.ant-typography {
    color: ${(props) => getThemeValue(props, "#ffffff", "#1A202C")} !important;
  }
`;

export const StyledText = styled(Text)<{ color?: string }>`
  color: ${(props) => {
    if (props.color) return props.color;
    return getThemeValue(props, "#CBD5E0", "#718096");
  }} !important;
  font-weight: 400;
`;

export const SecondaryText = styled(StyledText)`
  color: ${(props) => getThemeValue(props, "#CBD5E0", "#718096")} !important;
`;

export const SecondaryStyledText = styled(StyledText)`
  color: ${(props) => getThemeValue(props, "#CBD5E0", "#718096")} !important;
`;

export const HeaderContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 32px;
  padding: 20px 0;
  border-bottom: ${(props) => getThemeValue(props, "1px solid #4A5568", "1px solid #E2E8F0")};

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 20px;
    padding: 16px 0;
  }
`;

export const FilterContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  padding: 20px 24px;
  width: calc(100% - 32px);
  max-width: 100%;
  box-sizing: border-box;
  justify-content: flex-start;
  background: ${(props) => getThemeValue(props, "#4A5568", "#F7FAFC")};
  border-radius: 12px;
  margin: 16px;
  position: relative;
  z-index: 2;
  overflow: visible;

  /* Allow dropdowns to overflow */
  > * {
    position: relative;
    z-index: 10;
  }

  @media (max-width: 768px) {
    padding: 16px;
    gap: 12px;
    width: calc(100% - 24px);
    margin: 12px;
  }
`;

export const MetricsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 24px;
  padding: 20px 24px;
  width: calc(100% - 32px);
  max-width: 100%;
  box-sizing: border-box;
  margin: 0 16px;
  justify-content: center;
  align-items: center;
  background: ${(props) => getThemeValue(props, "#4A5568", "#F7FAFC")};
  border-radius: 12px;

  @media (max-width: 768px) {
    gap: 16px;
    padding: 16px;
    width: calc(100% - 24px);
    margin: 0 12px;
    flex-direction: column;
    align-items: flex-start;
  }
`;

export const MainContainer = styled.div`
  padding: 32px;
  color: ${(props) => getThemeValue(props, "#ffffff", "#1A202C")} !important;
  background: ${(props) => getThemeValue(props, "#1A202C", "#ffffff")} !important;
  min-height: 100vh;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  overflow-x: hidden;

  .ant-typography {
    color: ${(props) => getThemeValue(props, "#ffffff", "#1A202C")} !important;
  }

  @media (max-width: 768px) {
    padding: 20px 16px;
  }
`;

// Styled components for index.tsx
export const BackButton = styled(Button)`
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
`;

export const FullWidthSpace = styled(Space)`
  width: 100%;
`;

export const FullWidthVerticalSpace = styled(Space)`
  width: 100%;
`;

export const PageTitle = styled(Title)`
  margin: 0 !important;
`;

export const InheritText = styled(Text)`
  color: inherit !important;
`;

export const ChartSpaceWrapper = styled(Space)`
  width: 100%;
  max-width: 100%;
  padding: 0;
  box-sizing: border-box;
  overflow: visible;
`;

export const MetricBadge = styled(Space)<{ color: string }>`
  padding: 12px 20px;
  border-radius: 10px;
  background: ${(props) => {
    const colorMap: { [key: string]: string } = {
      "#52c41a": "rgba(82, 196, 26, 0.1)",
      "#1890ff": "rgba(24, 144, 255, 0.1)",
      "#fa8c16": "rgba(250, 140, 22, 0.1)",
      "#722ed1": "rgba(114, 46, 209, 0.1)",
    };
    return colorMap[props.color] || "rgba(82, 196, 26, 0.1)";
  }};
  border: ${(props) => {
    const colorMap: { [key: string]: string } = {
      "#52c41a": "1px solid rgba(82, 196, 26, 0.2)",
      "#1890ff": "1px solid rgba(24, 144, 255, 0.2)",
      "#fa8c16": "1px solid rgba(250, 140, 22, 0.2)",
      "#722ed1": "1px solid rgba(114, 46, 209, 0.2)",
    };
    return colorMap[props.color] || "1px solid rgba(82, 196, 26, 0.2)";
  }};
  transition: all 0.3s ease;
`;

export const MetricIcon = styled.span<{ color: string }>`
  color: ${(props) => props.color};
  font-size: 18px;
`;

export const MetricLabel = styled(Text)`
  font-weight: 500;
  color: inherit !important;
`;

export const MetricValue = styled(Text)<{ color: string }>`
  font-size: 16px;
  color: ${(props) => props.color} !important;
`;

export const ChartWrapper = styled.div`
  width: calc(100% - 32px);
  max-width: 100%;
  height: 380px;
  padding: 16px;
  margin: 0 16px 16px 16px;
  background-color: transparent;
  position: relative;
  z-index: 1;
  border-radius: 8px;
  box-sizing: border-box;
  overflow: hidden;
`;

// Styled components for CategorySalesSummary.tsx
export const SummarySpace = styled(Space)`
  width: 100%;
`;

export const HeaderSpace = styled(Space)`
  width: 100%;
  justify-content: space-between;
  flex-wrap: wrap;
`;

export const SummaryTitle = styled(Title)`
  margin: 0 !important;
  font-size: 20px !important;
  font-weight: 600 !important;
`;

export const SecondaryTextStyled = styled(SecondaryText)`
  font-size: 14px;
  display: block;
  margin-top: -8px;
`;

export const MetricsTitleSpace = styled(Space)`
  margin-top: 8px;
  flex-wrap: wrap;
`;

export const GradientTitle = styled(Title)`
  margin: 0 !important;
  background: linear-gradient(135deg, #1890ff 0%, #722ed1 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  font-size: 48px !important;
  font-weight: 700 !important;
  letter-spacing: -0.02em;
`;

export const TrendBadge = styled(Space)<{ isIncrease: boolean }>`
  padding: 8px 16px;
  border-radius: 8px;
  background: ${(props) =>
    props.isIncrease
      ? "rgba(82, 196, 26, 0.1)"
      : "rgba(255, 77, 79, 0.1)"};
  border: ${(props) =>
    props.isIncrease
      ? "1px solid rgba(82, 196, 26, 0.2)"
      : "1px solid rgba(255, 77, 79, 0.2)"};
`;

export const TrendIcon = styled.span<{ color: string }>`
  color: ${(props) => props.color};
  font-size: 18px;
`;

export const TrendText = styled(StyledText)<{ color: string }>`
  color: ${(props) => props.color} !important;
  font-size: 15px;
  font-weight: 600;
`;

export const MetricsWrapper = styled.div`
  width: 100%;
  max-width: 100%;
  padding: 16px 24px;
  box-sizing: border-box;
`;

export const KeyMetricsTitle = styled(StyledTitle)`
  margin-bottom: 16px !important;
`;

export const MetricIconWrapper = styled.div<{ color: string }>`
  padding: 8px;
  border-radius: 8px;
  background: ${(props) => `${props.color}20`};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

export const MetricIconStyled = styled.span<{ color: string }>`
  color: ${(props) => props.color};
  font-size: 20px;
`;

export const MetricTitleText = styled(StyledText)`
  font-size: 14px;
  font-weight: 600;
  white-space: normal;
  word-break: break-word;
  overflow-wrap: break-word;
  line-height: 1.4;
  margin: 0;
  flex: 1;
  min-width: 0;
`;

export const MetricValueTitle = styled(Title)<{ color: string }>`
  margin: 4px 0 0 0 !important;
  font-size: 24px !important;
  font-weight: 700 !important;
  color: ${(props) => props.color} !important;
`;

export const MetricSubtitleText = styled(StyledText)`
  font-size: 12px;
  margin-top: 4px;
  display: block;
`;

export const MetricCardSpace = styled(Space)<{ color?: string }>`
  margin-bottom: 16px;
  padding: 8px 12px;
  border-radius: 8px;
  width: 100%;
  flex-wrap: wrap;
  background: ${(props) => props.color ? `${props.color}15` : 'transparent'};
`;

// Styled components for CategorySalesRankingCarousel.tsx
export const CarouselWrapper = styled.div`
  margin-top: 32px;
`;

export const CarouselSecondaryText = styled(SecondaryStyledText)`
  margin-bottom: 16px;
  display: block;
`;

export const ToggleButton = styled(Button)`
  background: linear-gradient(135deg, #1890ff 0%, #722ed1 100%);
  border: none;
  border-radius: 8px;
  font-weight: 600;
  box-shadow: 0 4px 12px rgba(24, 144, 255, 0.3);
  transition: all 0.3s ease;
`;

export const SwiperStyled = styled(Swiper)`
  padding: 20px 0;
`;

export const SwiperSlideStyled = styled(SwiperSlide)`
  padding: 15px;
`;

export const RankingTitle = styled(StyledTitle)`
  margin-bottom: 8px !important;
`;
